import { useSelector } from 'store';
import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { FieldValues, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import {
  getDefaultShippingMethod,
  getShipmentCarrierAndService,
  handleErr,
} from 'utils/functions';
import _ from 'lodash';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import {
  Alert,
  FormControl,
  GridLegacy,
  ListSubheader,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  HookFormSelect,
  HookFormCheckbox,
  HookFormInput,
  HookFormAutocomplete,
} from 'ui-component/HookFormComponents';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import HookFormStockLocationSelect from 'ui-component/HookFormComponents/HookFormStockLocationSelect';
import {
  formConstants,
  formValidation,
} from 'views/inventory/ShippingPreferences/constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { ALL_APPS, ALL_FEATURE_IDS, cofactrSlug } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { ShippingMethods } from 'types/purchasing';
import { gridSpacing } from 'constants/themeConstants';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import { countryOptions } from 'constants/countryOptions';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import useSnackbar from 'hooks/useSnackbar';
import { StockLocationType } from 'types/inventory';

const ShippingPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();
  const isSubOrg = !!org?.createdFrom;

  const { hasFeaturePermission } = useAppAccessContext();
  const customShippingAccounts = hasFeaturePermission(
    ALL_FEATURE_IDS.CUSTOM_SHIPPING_ACCOUNTS
  );

  const tempOrgShippingAccounts = useFeature(
    FeatureFlags.tempOrgShippingAccounts
  ).on;

  const defaultShippingMethod = getDefaultShippingMethod({ org });

  const defaultValues = {
    [formConstants.defaultShippingMethod.name]: defaultShippingMethod,
    [formConstants.defaultShippingLocation.name]:
      org?.defaultShippingDestinationLocation?.id ?? '',
    [formConstants.defaultCofactrWarehouse.name]:
      org?.defaultCofactrWarehouse?.id ?? '',
    [formConstants.defaultShippingPartial.name]:
      org?.defaultShippingPartial ?? false,
    [formConstants.upsAccountNumber.name]: org?.upsAccountNumber ?? '',
    [formConstants.upsAccountCountry.name]: org?.upsAccountCountry ?? '',
    [formConstants.upsAccountPostalCode.name]: org?.upsAccountPostalCode ?? '',
    [formConstants.fedexAccountNumber.name]: org?.fedexAccountNumber ?? '',
    [formConstants.fedexAccountCountry.name]: org?.fedexAccountCountry ?? '',
    [formConstants.fedexAccountPostalCode.name]:
      org?.fedexAccountPostalCode ?? '',
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    setValue,
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (org) {
      reset(defaultValues);
    }
  }, [org]);

  const onSubmit = async (payload: FieldValues) => {
    if (isDirty) {
      try {
        if (payload?.defaultShippingMethod) {
          const { defaultShippingCarrier, defaultShippingService } =
            getShipmentCarrierAndService(payload.defaultShippingMethod);
          _.set(
            payload,
            formConstants.defaultShippingCarrier,
            defaultShippingCarrier
          );
          _.set(
            payload,
            formConstants.defaultShippingService,
            defaultShippingService
          );
        }
        delete payload.defaultShippingMethod;
        if (payload[formConstants.defaultShippingLocation.name] === '') {
          _.set(payload, formConstants.defaultShippingLocation.name, null);
        }
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar('Preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={2}>
              <GridLegacy item xs={12}>
                <HookFormSelect
                  control={control}
                  name={formConstants.defaultShippingMethod.name}
                  label={formConstants.defaultShippingMethod.label}
                  disabled={!isEditing}
                >
                  {ShippingMethods.map((method) =>
                    method.type === 'option' ? (
                      <MenuItem value={method.value} key={method.value}>
                        {method.label}
                      </MenuItem>
                    ) : (
                      <ListSubheader key={method.value}>
                        {method.label}
                      </ListSubheader>
                    )
                  )}
                </HookFormSelect>
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormStockLocationSelect
                  name={formConstants.defaultShippingLocation.name}
                  label={formConstants.defaultShippingLocation.label}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  contextualCreate
                  limitCreateLocationTypeToFacility
                  filters={[
                    {
                      field: 'locationType',
                      operator: '=',
                      value: StockLocationType.FACILITY,
                    },
                  ]}
                  disabled={!isEditing}
                />
                <HookFormStockLocationSelect
                  name={formConstants.defaultCofactrWarehouse.name}
                  label={formConstants.defaultCofactrWarehouse.label}
                  control={control}
                  errors={errors}
                  filters={[
                    {
                      field: 'locationType',
                      operator: '=',
                      value: StockLocationType.FACILITY,
                    },
                    {
                      // @ts-ignore because query filter typing does not support nested filters like this
                      field: 'org.slug',
                      operator: '=',
                      value: cofactrSlug,
                    },
                    {
                      field: 'warehouseShippingEnabled',
                      operator: '=',
                      value: true,
                    },
                  ]}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormCheckbox
                  control={control}
                  name={formConstants.defaultShippingPartial.name}
                  label={formConstants.defaultShippingPartial.label}
                  sx={{ mt: 0 }}
                  disabled={!isEditing}
                />
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  {formConstants.defaultShippingPartial.description}
                </Typography>
              </GridLegacy>
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.KITTING.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.KITTING.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="Shipping Preferences"
      />
      {tempOrgShippingAccounts && (
        <CardSectionWithAction
          contents={
            <FormControl fullWidth>
              <GridLegacy
                container
                sx={{ mt: gridSpacing }}
                columnSpacing={gridSpacing}
              >
                {isSubOrg && customShippingAccounts && (
                  <GridLegacy item xs={12}>
                    <Alert severity="info" sx={{ mb: gridSpacing }}>
                      This Organization is a Sub-Organization of another Cofactr
                      account. If you leave these settings blank, this account
                      will use the settings assigned to the parent account.
                    </Alert>
                  </GridLegacy>
                )}
                {customShippingAccounts ? (
                  <>
                    <GridLegacy item xs={4}>
                      <HookFormInput
                        errors={errors}
                        control={control}
                        name={formConstants.upsAccountNumber.name}
                        label={formConstants.upsAccountNumber.label}
                        disabled={!isEditing}
                      />
                    </GridLegacy>
                    <GridLegacy item xs={4}>
                      <HookFormAutocomplete<{ name: string; code: string }>
                        control={control}
                        getOptionLabel={(option) => option.name}
                        label={formConstants.upsAccountCountry.label}
                        name={formConstants.upsAccountCountry.name}
                        options={countryOptions}
                        errors={errors}
                        disabled={!isEditing}
                        handleChange={(option) => {
                          setValue(
                            formConstants.upsAccountCountry.name,
                            option.code
                          );
                        }}
                        initialValueMatcher={(value) =>
                          _.find(
                            countryOptions,
                            (country) => country.code === value
                          ) ?? null
                        }
                      />
                    </GridLegacy>
                    <GridLegacy item xs={4}>
                      <HookFormInput
                        errors={errors}
                        control={control}
                        name={formConstants.upsAccountPostalCode.name}
                        label={formConstants.upsAccountPostalCode.label}
                        disabled={!isEditing}
                      />
                    </GridLegacy>
                    <GridLegacy item xs={4}>
                      <HookFormInput
                        errors={errors}
                        control={control}
                        name={formConstants.fedexAccountNumber.name}
                        label={formConstants.fedexAccountNumber.label}
                        disabled={!isEditing}
                      />
                    </GridLegacy>
                    <GridLegacy item xs={4}>
                      <HookFormAutocomplete<{ name: string; code: string }>
                        control={control}
                        getOptionLabel={(option) => option.name}
                        label={formConstants.fedexAccountCountry.label}
                        name={formConstants.fedexAccountCountry.name}
                        options={countryOptions}
                        errors={errors}
                        disabled={!isEditing}
                        handleChange={(option) => {
                          setValue(
                            formConstants.fedexAccountCountry.name,
                            option.code
                          );
                        }}
                        initialValueMatcher={(value) =>
                          _.find(
                            countryOptions,
                            (country) => country.code === value
                          ) ?? null
                        }
                      />
                    </GridLegacy>
                    <GridLegacy item xs={4}>
                      <HookFormInput
                        errors={errors}
                        control={control}
                        name={formConstants.fedexAccountPostalCode.name}
                        label={formConstants.fedexAccountPostalCode.label}
                        disabled={!isEditing}
                      />
                    </GridLegacy>
                  </>
                ) : (
                  <GridLegacy item xs={12}>
                    <Alert severity="warning" sx={{ mb: gridSpacing }}>
                      Bill To 3rd Party Shipping Account is not enabled on your
                      account. Shipments will be processed using Cofactr&#39;s
                      shipping account and included in your monthly invoice.
                      Please contact Cofactr Success to upgrade to a plan that
                      includes this feature.
                    </Alert>
                  </GridLegacy>
                )}
              </GridLegacy>
            </FormControl>
          }
          action={
            isEditing ? (
              <SegmentLoadingButton
                type="submit"
                variant="contained"
                color="primary"
                size="medium"
                loading={isUpdatingOrg}
                permissionScope={{
                  app: ALL_APPS.KITTING.id,
                  accessLevel: appPermissionAccessLevels.edit,
                }}
              >
                {isDirty ? 'Save' : 'Cancel'}
              </SegmentLoadingButton>
            ) : (
              <SegmentButton
                type="button"
                variant="contained"
                onClick={() => setIsEditing(true)}
                sx={{ float: 'right' }}
                permissionScope={{
                  app: ALL_APPS.KITTING.id,
                  accessLevel: appPermissionAccessLevels.edit,
                }}
              >
                Edit
              </SegmentButton>
            )
          }
          title="Bill To 3rd Party Shipping Account"
          sx={{ mt: gridSpacing }}
        />
      )}
    </form>
  );
};

export default ShippingPreferences;
